import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { InfiniteScroll, NoResults } from 'basic-components';
import { Settings } from 'basic-components/icons';
import { DataStorageEnums } from 'dashboard-services/api/dataStorage';
import Query from 'dashboard-services/api/Query';
import { ReduxService } from 'dashboard-services/services';
import { Fetching, globalActions, userAuthQueries } from 'primary-components';

import { creatorSeriesActions } from 'actions/series';
import { basicSettingsActions } from 'actions/settings';
import getApiConfig from 'api/ApiConfig';
import { dataStorageQueries } from 'queries/datastorage';

import SymbolModal from 'primary/SymbolModal';

import ColumnsModal from './ColumnsModal';
import InfoButtonPortal from './InfoButtonPortal';
import SymbolRow from './SymbolRow';

import { When } from 'react-if';

import classNames from 'classnames';
import './Results.scss';

const LARGE_COLUMN_KEYS = creatorSeriesActions.LARGE_COLUMN_KEYS
const MEDIUM_COLUMN_KEYS = creatorSeriesActions.MEDIUM_COLUMN_KEYS
const ORDERED_COLUMN_KEYS = basicSettingsActions.ORDERED_COLUMN_KEYS
const orderedColumnKeyNames = ORDERED_COLUMN_KEYS.map(column => column.filterKey)
const getColumnDefinition = ({ columnKey, symbolKeys }) => ORDERED_COLUMN_KEYS.find(column => column.filterKey === columnKey) || {
  label: columnKey,
  filterKey: columnKey,
  type: symbolKeys.includes(columnKey) ? DataStorageEnums.KEY_TYPE.SYMBOL : DataStorageEnums.KEY_TYPE.METADATA,
  isHidden: false
}
const EMPTY_ARRAY = []
const useSearchSymbols = ({ query, symbolsFilter, metadatasFilter, groupFilter, userDefinedFilters, setTotalSize, setSymbols }) => {
  const dispatch = useDispatch(),
        buildedQuery = useMemo(() => creatorSeriesActions.getSearchQuery({ query, symbolsFilter, metadatasFilter, groupName: groupFilter }), [groupFilter, metadatasFilter, query, symbolsFilter]),
        sortBy = useMemo(() => creatorSeriesActions.getSortString(userDefinedFilters), [userDefinedFilters]),
        onSuccess = useCallback(response => dispatch(creatorSeriesActions.onEnvsSuccess(response)),[dispatch]),
        getQueryBuilder = useCallback(() => new Query.Builder(dispatch(getApiConfig()))
          .withFetching(false)
          .withOnSuccess(onSuccess)
          .withShouldDisplayError(false)
        , [dispatch, onSuccess]),
        { data: symbolsRaw, isPending, isFetchingNextPage, fetchNextPage, hasNextPage } = dataStorageQueries.useSearchSymbolsEnvs({
          query: buildedQuery,
          getQueryBuilder,
          sortBy,
          size: 100,
          enabled: !!(query || Object.keys(metadatasFilter || {}).length > 0 || Object.keys(symbolsFilter).length > 0 || groupFilter),
          shouldRefetch: false,
        }),
        totalSize = useMemo(() => symbolsRaw?.pages[symbolsRaw?.pages?.length - 1]?.totalSize, [symbolsRaw?.pages]),
        symbols = useMemo(() => (symbolsRaw?.pages || EMPTY_ARRAY).flatMap(p => p?.items || EMPTY_ARRAY), [symbolsRaw?.pages]),
        toReturn = useMemo(() => ({ isPending, isFetchingNextPage, fetchNextPage, hasNextPage, symbols, totalSize }), [fetchNextPage, hasNextPage, isFetchingNextPage, isPending, symbols, totalSize])

  useEffect(() => {
    if(totalSize) {
      setTotalSize(totalSize)
    }
  }, [setTotalSize, totalSize])

  useEffect(() => {
    if(symbols) {
      setSymbols(symbols)
    }
  }, [setSymbols, symbols])

  return toReturn
}

const useUserDefinedFilters = ({ userDefinedFiltersRaw }) => {
  const filters = useSelector(state => state.seriesState.creatorState.filters),
        excelSeriesOptimalization = useSelector(state => state.settingsState.excelSeriesOptimalization),
        userDefinedFilters = useMemo(() => {
          if(!excelSeriesOptimalization) {
            return userDefinedFiltersRaw
          }

          return userDefinedFiltersRaw.map(udf => {
            const hasKey = Object.prototype.hasOwnProperty.call(filters, udf.filterKey),
                  value = filters[udf.filterKey]

            if(hasKey && ReduxService.nonNullOrUndefined(value)) {
              return ReduxService.updateObject(udf, { shouldNotBeDisplayed: true });
            }

            return udf;
          })
        }, [excelSeriesOptimalization, filters, userDefinedFiltersRaw])

  return userDefinedFilters;
}

const useInfoButton = () => {
  const [infoSymbol, setInfoSymbol] = useState(),
        [hoveredSymbol, setHoveredSymbol] = useState(),
        [hoveredRowIndex, setHoveredRowIndex] = useState(),
        [isBodyScrolling, setIsBodyScrolling] = useState(false),
        [infoButtonPosition, setInfoButtonPosition] = useState({ right: 0, top: -32 }),
        hoveredRowRef = useRef(),
        scrollTimeoutRef = useRef(),
        scrollContRef = useRef(),
        openInfoModal = useCallback(symbol => setInfoSymbol({ ...symbol, pattern: false }), []),
        closeInfoModal = useCallback(() => setInfoSymbol(undefined), []),
        updateInfoButtonPosition = useCallback(row => {
          const body = scrollContRef.current,
                rowRect = row?.getBoundingClientRect(),
                viewportRect = body?.parentElement?.getBoundingClientRect()

          if(rowRect) {
            setInfoButtonPosition({
              right: viewportRect ? window.innerWidth - viewportRect.right : 0,
              top: rowRect.top + (rowRect.height - 32) / 2,
            })
          }
        }, []),
        onRowHover = useCallback(({ index, row, symbol }) => {
          if(!symbol) {
            setHoveredSymbol(undefined)
            setHoveredRowIndex(undefined)
            hoveredRowRef.current = undefined
            return
          }
          hoveredRowRef.current = row
          setHoveredSymbol(symbol)
          setHoveredRowIndex(index)
          updateInfoButtonPosition(row)
        }, [updateInfoButtonPosition]),
        onBodyScroll = useCallback(() => {
          setIsBodyScrolling(true)
          updateInfoButtonPosition(hoveredRowRef.current)
          clearTimeout(scrollTimeoutRef.current)
          scrollTimeoutRef.current = window.setTimeout(() => setIsBodyScrolling(false), 200)
        }, [updateInfoButtonPosition]),
        toReturn = useMemo(() => ({ closeInfoModal, hoveredRowIndex, hoveredSymbol, infoButtonPosition, infoSymbol, isBodyScrolling, onBodyScroll, onRowHover, openInfoModal, scrollContRef }), [closeInfoModal, hoveredRowIndex, hoveredSymbol, infoButtonPosition, infoSymbol, isBodyScrolling, onBodyScroll, onRowHover, openInfoModal])

  useEffect(() => () => {
    clearTimeout(scrollTimeoutRef.current)
  }, [])

  return toReturn
}

const EMPTY_OBJ = {};
const useResultColumns = ({ savedColumnsSetting, symbols, userDefinedFilters }) => {
  const symbolKeys = useMemo(() => symbols.flatMap(symbol => Object.keys(symbol.symbols || {})).toUnique(), [symbols]),
        metadataKeys = useMemo(() => symbols.flatMap(symbol => Object.keys(symbol.metadata || {})).toUnique(), [symbols]),
        sourceColumnAvailable = useMemo(() => symbols.some(symbol => symbol.source !== undefined), [symbols]),
        availableColumnKeys = useMemo(() => [...symbolKeys, ...metadataKeys, ...(sourceColumnAvailable ? ["Source"] : [])].toUnique(), [metadataKeys, sourceColumnAvailable, symbolKeys]),
        savedColumnKeys = useMemo(() => savedColumnsSetting.map(column => column.filterKey), [savedColumnsSetting]),
        initialOrderedColumnKeys = useMemo(() => [...(savedColumnKeys.length > 0 ? savedColumnKeys : orderedColumnKeyNames), ...availableColumnKeys].toUnique(), [availableColumnKeys, savedColumnKeys]),
        selectedFilterColumnKeys = useMemo(() => userDefinedFilters.filter(filter => filter.shouldNotBeDisplayed).map(filter => filter.filterKey || filter.label), [userDefinedFilters]),
        modalColumns = useMemo(() => initialOrderedColumnKeys.map(columnKey => {
          const column = savedColumnsSetting.find(savedColumn => savedColumn.filterKey === columnKey) || getColumnDefinition({ columnKey, metadataKeys, symbolKeys }),
                isSelectedAsFilter = selectedFilterColumnKeys.includes(columnKey),
                isNotAvailable = !availableColumnKeys.includes(columnKey)
          return {
            ...column,
            isSelectedAsFilter,
            isNotAvailable
          }
        }), [availableColumnKeys, initialOrderedColumnKeys, metadataKeys, savedColumnsSetting, selectedFilterColumnKeys, symbolKeys]),
        visibleOrderedColumns = useMemo(() => modalColumns.filter(column => !column.isHidden && !column.isSelectedAsFilter && !column.isNotAvailable), [modalColumns]),
        userDefinedColumnKeys = useMemo(() => userDefinedFilters.filter(udf => !orderedColumnKeyNames.includes(udf.label) && !orderedColumnKeyNames.includes(udf.filterKey)), [userDefinedFilters]),
        visibleOrderedColumnKeys = useMemo(() => modalColumns
          .filter(column => !column.isSelectedAsFilter && !column.isNotAvailable)
          .map(column => column.filterKey), [modalColumns]),
        toReturn = useMemo(() => ({ metadataKeys, modalColumns, symbolKeys, userDefinedColumnKeys, visibleOrderedColumnKeys, visibleOrderedColumns }), [metadataKeys, modalColumns, symbolKeys, userDefinedColumnKeys, visibleOrderedColumnKeys, visibleOrderedColumns])
  return toReturn
}

const useSaveColumns = ({ onClose }) => {
  const dispatch = useDispatch(),
        getQueryBuilder = useCallback(() => new Query.Builder(dispatch(getApiConfig())), [dispatch]),
        { mutateAsync: saveColumns } = userAuthQueries.useUpdateCurrentUserSettings({ getQueryBuilder }),
        onSaveColumns = useCallback(columns => {
          const columnsToSave = columns.map(column => ({ ...column })
            .removeProp("isNotAvailable")
            .removeProp("isSelectedAsFilter"))
          return saveColumns({ settings: { seriesResultsColumns: JSON.stringify(columnsToSave) } })
            .then(() => dispatch(basicSettingsActions.onChange(columnsToSave, "seriesResultsColumns")))
            .then(() => dispatch(globalActions.getMessageStore()).success("Columns have been saved."))
            .then(onClose)
        }, [dispatch, onClose, saveColumns])

  return onSaveColumns
}

const Results = ({ metadatasFilter = EMPTY_OBJ, symbolsFilter = EMPTY_OBJ, groupFilter = undefined, getResultsStyle, setTotalSize, setSymbols }) => {
  const dispatch = useDispatch(),
        [isColumnsModalOpen, setIsColumnsModalOpen] = useState(false),
        savedColumnsSetting = useSelector(state => state.settingsState.seriesResultsColumns) || EMPTY_ARRAY,
        query = useSelector(state => state.seriesState.creatorState.query),
        userDefinedFiltersRaw = useSelector(state => state.seriesState.creatorState.userDefinedFilters),
        selectedSymbols = useSelector(state => state.seriesState.creatorState.selectedSymbols),
        userDefinedFilters = useUserDefinedFilters({ userDefinedFiltersRaw }),
        { fetchNextPage, hasNextPage, isFetchingNextPage, isPending, symbols, totalSize } = useSearchSymbols({
          query,
          symbolsFilter,
          metadatasFilter,
          groupFilter,
          userDefinedFilters,
          setTotalSize,
          setSymbols,
        }),
        symbolsLength = useMemo(() => symbols?.length, [symbols]),
        { metadataKeys, modalColumns, symbolKeys, userDefinedColumnKeys, visibleOrderedColumnKeys, visibleOrderedColumns } = useResultColumns({ savedColumnsSetting, symbols, userDefinedFilters }),
        groupColumnAvailable = useMemo(() => symbols.some(symbol => symbol.groupName !== undefined), [symbols]),
        arePatternsSelected = useMemo(() => selectedSymbols.some(s => s.pattern === "true" || s.pattern === undefined), [selectedSymbols]),
        selectedSource = useMemo(() => selectedSymbols?.length > 0 ? selectedSymbols[0]?.source : undefined, [selectedSymbols]),
        addSymbol = useCallback(symbol => {
          if(!symbol) {
            return;
          }
          dispatch(creatorSeriesActions.swapSelectedSymbol(ReduxService.updateObject(symbol, { pattern: "false" })))
        }, [dispatch]),
        openBasket = useCallback(() => dispatch(creatorSeriesActions.onChange(true, "isBasketOpen")), [dispatch]),
        onRowClick = useCallback(symbolIndex => {
          const symbol = symbols[symbolIndex]
          dispatch(creatorSeriesActions.onAddRow({ symbol, selectedSymbols, addToBasket: addSymbol, arePatternsSelected }))
        }, [addSymbol, arePatternsSelected, dispatch, selectedSymbols, symbols]),
        resultsStyle = useMemo(() => getResultsStyle(), [getResultsStyle]),
        { 
          closeInfoModal,
          hoveredRowIndex,
          hoveredSymbol,
          infoButtonPosition,
          infoSymbol,
          isBodyScrolling,
          onBodyScroll,
          onRowHover,
          openInfoModal,
          scrollContRef
        } = useInfoButton(),
        getScrollParent = useCallback(() => scrollContRef.current, [scrollContRef]),
        loadMore = useCallback(() => {
          if(!isFetchingNextPage) {
            fetchNextPage()
          }
        }, [fetchNextPage, isFetchingNextPage]),
        swapIsColumnsModalOpen = useCallback(() => setIsColumnsModalOpen(isOpen => !isOpen), []),
        onSaveColumns = useSaveColumns({ onClose: swapIsColumnsModalOpen }),
        handleOnKeyDown = useCallback(e => {
          if ((e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            swapIsColumnsModalOpen()
          }
        }, [swapIsColumnsModalOpen])

  return (
    <div
        className="ng-office-app__authed__content__results"
        style={resultsStyle}
    >
      <div className="ng-office-app__authed__content__results__headers">
        <When condition={symbolsLength > 0}>
          {() => (
            <Fragment>
              <div
                  aria-label="Default columns selection"
                  className="ng-office-app__authed__content__results__headers__config"
                  onClick={swapIsColumnsModalOpen}
                  onKeyDown={handleOnKeyDown}
                  tabIndex={0}
              >
                <Settings
                    color="blue-bright"
                    height={16}
                    width={16}
                />
              </div>
              {visibleOrderedColumns.map(column => (
                <div
                    className={classNames(
                      "ng-office-app__authed__content__results__headers__header",
                      { "ng-office-app__authed__content__results__headers__header--medium": MEDIUM_COLUMN_KEYS.includes(column.filterKey) },
                      { "ng-office-app__authed__content__results__headers__header--large": LARGE_COLUMN_KEYS.includes(column.filterKey) }
                    )}
                    key={`ordered-${column.filterKey}`}
                >
                  {column.label}
                </div>
              ))}
              {symbolKeys.filter(symbolKey => !visibleOrderedColumnKeys.includes(symbolKey) && userDefinedFilters.findIndex(udf => udf.type === creatorSeriesActions.FILTER_TYPE.SYMBOL && udf.filterKey === symbolKey) === -1).map(symbolKey => (
                <div
                    className={classNames(
                      "ng-office-app__authed__content__results__headers__header",
                      { "ng-office-app__authed__content__results__headers__header--medium": MEDIUM_COLUMN_KEYS.includes(symbolKey) },
                      { "ng-office-app__authed__content__results__headers__header--large": LARGE_COLUMN_KEYS.includes(symbolKey) }
                    )}
                    key={`symbol-${symbolKey}`}
                >
                  {symbolKey}
                </div>
              ))}
              {metadataKeys.filter(metadataKey => !visibleOrderedColumnKeys.includes(metadataKey) && userDefinedFilters.findIndex(udf => udf.type === creatorSeriesActions.FILTER_TYPE.METADATA && udf.filterKey === metadataKey) === -1).map(metadataKey => (
                <div
                    className={classNames(
                      "ng-office-app__authed__content__results__headers__header",
                      { "ng-office-app__authed__content__results__headers__header--medium": MEDIUM_COLUMN_KEYS.includes(metadataKey) },
                      { "ng-office-app__authed__content__results__headers__header--large": LARGE_COLUMN_KEYS.includes(metadataKey) }
                    )}
                    key={`metadata-${metadataKey}`}
                >
                  {metadataKey}
                </div>
              ))}
              <When condition={groupColumnAvailable && userDefinedFilters.findIndex(udf => udf.type === creatorSeriesActions.FILTER_TYPE.GROUP_NAME) === -1}>
                {() => (
                  <div className="ng-office-app__authed__content__results__headers__header">
                    Group
                  </div>
                )}
              </When>
            </Fragment>
          )}
        </When>
      </div>
      <div
          className="ng-office-app__authed__content__results__body"
          onScroll={onBodyScroll}
          ref={scrollContRef}
      >
        <Fetching isFetching={isPending || isFetchingNextPage}/>
        <InfiniteScroll
            className="ng-office-app__authed__content__results__body__scroll"
            getScrollParent={getScrollParent}
            hasMore={hasNextPage}
            initialLoad={false}
            loadMore={loadMore}
            threshold={800}
            useWindow={false}
        >
          {symbols.map((symbol, symbolIndex) => {
            const {
              isDirectlySelected,
              isPatternSelected
            } = creatorSeriesActions.isInBasket(symbol, selectedSymbols)
            return (
              <SymbolRow
                  {...symbol}
                  configuredColumnKeys={visibleOrderedColumnKeys}
                  disabled={selectedSource && symbol.source !== selectedSource}
                  isHovered={hoveredRowIndex === symbolIndex}
                  isPatternSelected={isPatternSelected}
                  isSelected={isDirectlySelected}
                  key={symbolIndex}
                  metadataKeys={metadataKeys}
                  onRowClick={isPatternSelected && !isDirectlySelected ? openBasket : onRowClick}
                  onRowHover={onRowHover}
                  orderedColumnKeys={visibleOrderedColumns}
                  scrollContRef={scrollContRef}
                  symbolIndex={symbolIndex}
                  symbolKeys={symbolKeys}
                  userDefinedColumnKeys={userDefinedColumnKeys}
                  userDefinedFilters={userDefinedFilters}
              />
            )
          })}
        </InfiniteScroll>
        <When condition={symbolsLength === 0 && (query || Object.keys(metadatasFilter || {}).length > 0 || Object.keys(symbolsFilter || {}).length > 0 || groupFilter) && (!isPending && !isFetchingNextPage)}>
          {() => (
            <NoResults/>
          )}
        </When>
      </div>
      <When condition={infoSymbol !== undefined}>
        {() => (
          <SymbolModal
              onClose={closeInfoModal}
              symbol={infoSymbol}
          />
        )}
      </When>
      <When condition={hoveredSymbol !== undefined && !isBodyScrolling}>
        {() => (
          <InfoButtonPortal
              infoButtonPosition={infoButtonPosition}
              onClick={openInfoModal}
              rowIndex={hoveredRowIndex}
              symbol={hoveredSymbol}
          />
        )}
      </When>
      <div className="ng-office-app__authed__content__results__footer">
        {isPending ? "" : `${Number(symbolsLength > totalSize ? totalSize : symbolsLength).toLocaleString("en-EN")} of ${Number(totalSize).toLocaleString("en-EN")}`}
      </div>
      <When condition={isColumnsModalOpen}>
        {() => (
          <ColumnsModal
              columns={modalColumns}
              onClose={swapIsColumnsModalOpen}
              onSave={onSaveColumns}
          />
        )}
      </When>
    </div>
  )
}

Results.propTypes = {
  getResultsStyle: PropTypes.func.isRequired,
  groupFilter: PropTypes.string,
  metadatasFilter: PropTypes.object,
  setSymbols: PropTypes.func.isRequired,
  setTotalSize: PropTypes.func.isRequired,
  symbols: PropTypes.array,
  symbolsFilter: PropTypes.object,
  totalSize: PropTypes.number,
}

export default Results;
